import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader, StatusBadge, EmptyState } from '@/components/Shared';
import { money, shortDate } from '@/lib/format';
import { ClipboardList, Check, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DangerConfirm } from '@/components/DangerConfirm';

const REFUND_METHODS = ['Cash Refund', 'Zelle Refund', 'Venmo Refund', 'Check Refund', 'Bank Transfer Refund', 'Store Credit', 'Balance Credit', 'Other'];

export default function Requests() {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('pending');
  const [customers, setCustomers] = useState([]);
  const [resolve, setResolve] = useState({ open: false, request: null, note: '', customer_id: '', credit: '', destination: 'sellable', discount: '', commission_percent: '', commission_amount: '', refund_type: 'full', refund_method: 'Cash Refund', commission_reversal: '' });
  const [delTarget, setDelTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const doDeleteRequest = async (password) => {
    setDelBusy(true);
    try {
      await api.post(`/requests/${delTarget.id}/delete`, { password, confirm: 'CONFIRM' });
      toast.success('Request deleted');
      setDelTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setDelBusy(false); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get('/requests', { params: { status: tab === 'all' ? undefined : tab } });
    setRequests(data);
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get('/customers').then(({ data }) => setCustomers(data)); }, [isAdmin]);

  const openApprove = (r) => {
    const def = customers.find((c) => c.is_default);
    const fullAmt = r.quantity && r.unit_price ? String((r.quantity * r.unit_price).toFixed(2)) : '';
    setResolve({ open: true, request: r, note: '', customer_id: r.customer_id || def?.id || (customers[0]?.id || ''), credit: fullAmt, destination: 'sellable', discount: '', commission_percent: '', commission_amount: '', refund_type: 'full', refund_method: 'Cash Refund', commission_reversal: '' });
  };

  const approve = async () => {
    const r = resolve.request;
    const isReturn = r?.request_type === 'return';
    if (!resolve.customer_id) { toast.error('Select a customer'); return; }
    if (isReturn && resolve.refund_type === 'partial' && (resolve.credit === '' || Number(resolve.credit) <= 0)) { toast.error('Enter the partial refund amount'); return; }
    try {
      const payload = { admin_note: resolve.note, customer_id: resolve.customer_id };
      if (isReturn) {
        payload.return_destination = resolve.destination;
        payload.refund_type = resolve.refund_type;
        if (resolve.refund_type !== 'none') {
          payload.return_credit = Number(resolve.credit || 0);
          payload.refund_method = resolve.refund_method;
        }
        if (resolve.commission_reversal !== '') payload.commission_reversal = Math.max(0, Number(resolve.commission_reversal));
      } else {
        if (resolve.discount !== '') payload.discount_percent = Math.max(0, Math.min(50, Number(resolve.discount)));
        if (resolve.commission_amount !== '') payload.commission_amount = Math.max(0, Number(resolve.commission_amount));
        else if (resolve.commission_percent !== '') payload.commission_percent = Math.max(0, Math.min(100, Number(resolve.commission_percent)));
      }
      await api.post(`/requests/${r.id}/approve`, payload);
      toast.success(isReturn ? 'Return approved — processed & inventory updated' : 'Approved — invoice generated & stock updated');
      setResolve({ open: false }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reject = async (r) => {
    const note = window.prompt('Reason for rejection (optional):') || '';
    try { await api.post(`/requests/${r.id}/reject`, { admin_note: note }); toast.success('Rejected'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div>
      <PageHeader title={isAdmin ? 'Product Requests' : 'My Requests'} subtitle={isAdmin ? 'Approve or reject employee product requests' : 'Track your submitted requests'} testid="requests-header" />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="requests-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="requests-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="requests-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-0 overflow-hidden">
        {requests.length === 0 ? <div className="p-6"><EmptyState icon={ClipboardList} title="No requests" description="Requests will appear here." /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>{isAdmin && <TableHead>Employee</TableHead>}<TableHead>Type</TableHead><TableHead>Product</TableHead><TableHead>SKU</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead></TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} data-testid="request-row">
                    <TableCell>{shortDate(r.created_at)}</TableCell>
                    {isAdmin && <TableCell>{r.employee_name}</TableCell>}
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${r.request_type === 'return' ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'}`} data-testid="request-type-badge">
                        {r.request_type === 'return' ? 'Return' : 'Buy'}
                      </span>
                    </TableCell>
                    <TableCell>{r.product_name}{r.request_type === 'return' && r.reason ? <div className="text-xs text-muted-foreground">Reason: {r.reason}</div> : null}</TableCell>
                    <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.unit_price)}</TableCell>
                    <TableCell><StatusBadge kind="request" value={r.status} /></TableCell>
                    {isAdmin && <TableCell>
                      <div className="flex gap-1">
                        {r.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => openApprove(r)} data-testid="requests-approve-button"><Check className="h-4 w-4" /></Button>
                            <Button size="sm" variant="outline" onClick={() => reject(r)} data-testid="requests-reject-button"><X className="h-4 w-4" /></Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setDelTarget(r)} data-testid="request-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={resolve.open} onOpenChange={(o) => setResolve({ ...resolve, open: o })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{resolve.request?.request_type === 'return' ? 'Approve Return Request' : 'Approve Buy Request'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {resolve.request?.request_type === 'return' ? (
              <p className="text-sm text-muted-foreground">Choose the refund option and where returned stock goes. Balance is reduced first (never below $0 unless Store/Balance Credit), any remainder is refunded, and any commission you enter is reversed for the employee.</p>
            ) : (
              <p className="text-sm text-muted-foreground">An invoice will be generated for the selected customer and stock will be reduced.</p>
            )}
            <div>
              <label className="text-sm font-medium">Customer</label>
              <Select value={resolve.customer_id} onValueChange={(v) => setResolve({ ...resolve, customer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="approve-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {resolve.request?.request_type === 'return' ? (
              <>
                <div>
                  <label className="text-sm font-medium">Refund Option</label>
                  <Select value={resolve.refund_type} onValueChange={(v) => setResolve({ ...resolve, refund_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="approve-refund-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Refund</SelectItem>
                      <SelectItem value="partial">Partial Refund</SelectItem>
                      <SelectItem value="none">No Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {resolve.refund_type !== 'none' && (
                  <>
                    <div>
                      <label className="text-sm font-medium">{resolve.refund_type === 'partial' ? 'Partial Refund / Credit Amount ($)' : 'Refund / Credit Amount ($)'}</label>
                      <Input type="number" min="0" step="0.01" value={resolve.credit} onChange={(e) => setResolve({ ...resolve, credit: e.target.value })} className="mt-1.5" data-testid="approve-return-credit-input" />
                      <p className="text-xs text-muted-foreground mt-1">Applies to the customer's balance first (down to $0). Any remaining amount becomes a refund. Choose Store/Balance Credit to keep it all as account credit.</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Refund Method</label>
                      <Select value={resolve.refund_method} onValueChange={(v) => setResolve({ ...resolve, refund_method: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="approve-refund-method-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{REFUND_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-sm font-medium">Commission to Reverse ($)</label>
                  <Input type="number" min="0" step="0.01" value={resolve.commission_reversal} onChange={(e) => setResolve({ ...resolve, commission_reversal: e.target.value })} placeholder="0.00" className="mt-1.5" data-testid="approve-commission-reversal-input" />
                  <p className="text-xs text-muted-foreground mt-1">Clawed back from {resolve.request?.employee_name}. Shown as an adjustment on the credit note & commissions.</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Return Destination</label>
                  <Select value={resolve.destination} onValueChange={(v) => setResolve({ ...resolve, destination: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="approve-return-destination-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sellable">Return to sellable inventory</SelectItem>
                      <SelectItem value="damaged">Return to damaged inventory</SelectItem>
                      <SelectItem value="scrap">Return to defective / scrap</SelectItem>
                      <SelectItem value="none">Do not return to inventory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">Discount % (0–50)</label>
                  <Input type="number" min="0" max="50" step="0.5" value={resolve.discount} onChange={(e) => setResolve({ ...resolve, discount: e.target.value })} placeholder="customer default" className="mt-1.5" data-testid="approve-discount-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Commission % (0–100)</label>
                  <Input type="number" min="0" max="100" step="0.5" value={resolve.commission_percent} onChange={(e) => setResolve({ ...resolve, commission_percent: e.target.value })} placeholder="0" disabled={resolve.commission_amount !== ''} className="mt-1.5" data-testid="approve-commission-percent-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Commission $ (overrides %)</label>
                  <Input type="number" min="0" step="0.01" value={resolve.commission_amount} onChange={(e) => setResolve({ ...resolve, commission_amount: e.target.value })} placeholder="0.00" className="mt-1.5" data-testid="approve-commission-amount-input" />
                </div>
              </div>
            )}
            <div><label className="text-sm font-medium">Note (optional)</label><Textarea value={resolve.note} onChange={(e) => setResolve({ ...resolve, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResolve({ open: false })}>Cancel</Button><Button onClick={approve} data-testid="approve-confirm-button">{resolve.request?.request_type === 'return' ? 'Approve & Issue Credit' : 'Approve & Generate Invoice'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DangerConfirm
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title="Delete this request?"
        description="Permanently deletes this request. Any invoice already generated from it is not affected. This cannot be undone."
        affectedText={delTarget ? `${(delTarget.request_type || 'buy')} request · ${delTarget.product_name} · ${delTarget.employee_name}` : ''}
        onConfirm={doDeleteRequest}
        busy={delBusy}
        confirmLabel="Delete Request"
      />
    </div>
  );
}
